/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {
    FC,
    ContainerProps,
    CanBeSignals,
    responsiveSkipChildren,
    $Container,
    getProp,
    patchEvents,
} from '@gongxigames/framework/core/fragments/BuilderY';
import {Store, Signal} from '@gongxigames/framework/core/fragments/store/Store';
import {IHitArea} from 'pixi.js';

export type ButtonDrawComponentProps = {
    focused: Signal<boolean>;
    pressed: Signal<boolean>;
    enabled: Signal<boolean>;
    selector: Signal<string>;
};

export type ButtonDrawComponentType = FC<ButtonDrawComponentProps>;

export type ButtonProps = Omit<ContainerProps, 'hitArea' | 'children' | 'interactive' | 'buttonMode'> & CanBeSignals<{
    name?: string;
    hitArea: IHitArea;
    disabled?: boolean;
    action?: () => void;
    pointerUp?: (hover: boolean) => void;
    pointerDown?: () => void;
}> & {
    children: ButtonDrawComponentType;
};

export const $Button: FC<ButtonProps> = (props) => {
    const focused = Store.signal(false);
    const pressed = Store.signal(false);
    const enabled = Store.computed(() => !getProp(props.disabled));

    const selector = Store.computed(() => {
        if (!enabled.value) {
            return 'disabled';
        }

        if (pressed.value) {
            return 'pressed';
        }

        if (focused.value) {
            return 'focused';
        }

        return 'normal';
    });

    const setHoverTrue = () => focused.value = true;
    const setHoverFalse = () => focused.value = false;
    const setPressTrue = () => pressed.value = true;
    const setPressFalse = () => pressed.value = false;
    const runActionCallBack = () => getProp(props.action)?.();
    const runPointerDownCallBack = () => getProp(props.pointerDown)?.();
    const runPointerUpOverCallBack = () => getProp(props.pointerUp)?.(true);
    const runPointerUpOutsideCallBack = () => getProp(props.pointerUp)?.(false);

    props.events ??= {};
    patchEvents(props.events, {
        pointerover: setHoverTrue,
        pointerout: setHoverFalse,
        pointerdown: [setPressTrue, runPointerDownCallBack],
        pointerup: [setPressFalse, runPointerUpOverCallBack],
        pointertap: runActionCallBack,
        pointerupoutside: [setPressFalse, runPointerUpOutsideCallBack],
    });

    responsiveSkipChildren(props);

    return <$Container
        interactive={enabled}
        buttonMode={enabled}
        {...props}
    >
        {props.children({focused, enabled, pressed, selector})}
    </$Container>;
};
